import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject, input, output } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, map, shareReplay, take } from 'rxjs';
import { v4 as uuid } from 'uuid';

import { ProductContextFacade } from 'ish-core/facades/product-context.facade';
import { PricePipe } from 'ish-core/models/price/price.pipe';
import { Warranty } from 'ish-core/models/warranty/warranty.model';
import { ProductWarrantyDetailsComponent } from 'ish-shared/components/product/product-warranty-details/product-warranty-details.component';

/**
 * The Product Warranty Component displays either a form element (select box or radio buttons), so the user can select a warranty or displays the selected warranty.
 * The available warranty options are provided by the product via the product context facade instance.
 * If the user selects a warranty a submitWarranty event is emitted.
 *
 * @example
 * <ish-product-warranty
 *   [selectedWarrantySku]="pli.warranty?.sku"
 *   viewType="select" />
 */

@Component({
  selector: 'ish-product-warranty',
  templateUrl: './product-warranty.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, PricePipe, ProductWarrantyDetailsComponent],
})
export class ProductWarrantyComponent implements OnInit {
  private productContext = inject(ProductContextFacade);
  private translateService = inject(TranslateService);

  // preselect a warranty
  readonly selectedWarrantySku = input<string>(undefined);
  readonly viewType = input<'radio' | 'select' | 'display'>('radio');

  readonly submitWarranty = output<string>();

  uuid: string = uuid();

  warranties$: Observable<Warranty[]>;
  private noWarranty: Warranty;

  ngOnInit() {
    this.noWarranty = {
      id: '',
      name: this.translateService.instant('product.warranty.no_protection_plan'),
      price: undefined,
    };

    this.warranties$ = this.productContext.select('product').pipe(
      map(product => (product.availableWarranties?.length ? [...product.availableWarranties, this.noWarranty] : [])),
      shareReplay(1)
    );
  }

  updateWarranty(warranty: string | EventTarget) {
    if (typeof warranty === 'string') {
      this.submitWarranty.emit(warranty);
    } else {
      this.submitWarranty.emit(warranty ? (warranty as HTMLDataElement).value : '');
    }
  }

  getSelectedWarranty$(warrantySku: string) {
    return this.warranties$.pipe(
      map(warranties => warranties.find(warranty => warranty.id === warrantySku)),
      take(1)
    );
  }
}
